/**
 * The offline outbox.
 *
 * A salesperson in a shop with no signal must be able to book an order, and
 * that order must reach the server exactly once when the phone reconnects.
 *
 * How that is achieved:
 *
 *   * Every mutation gets a **client-generated UUID** before it leaves the
 *     screen. The server treats a repeat of the same UUID as a duplicate and
 *     returns the original record instead of creating a second one. So a retry
 *     after a timeout is safe — which matters, because a request that timed out
 *     may or may not have been processed.
 *
 *   * Items drain **oldest-first and strictly in order**. An order must reach
 *     the server before the POD for that order, and a customer must exist
 *     before an order references them. Parallel draining would break both.
 *
 *   * A **4xx is permanent** and moves the item to `failed` for the user to
 *     see. A 5xx or network error is transient and retried with backoff.
 *     Retrying a validation error forever just hides it.
 */

import * as idb from './idb';

export type OutboxStatus = 'pending' | 'syncing' | 'failed' | 'done';

export interface OutboxItem {
  id: string;
  /** Sent as clientUuid so the server can dedupe. Same value across retries. */
  clientUuid: string;
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  body: unknown;
  /** Shown in the pending list, e.g. "Order — Sharma Kirana". */
  label: string;
  kind: 'order' | 'visit' | 'pod' | 'payment' | 'customer' | 'delivery_confirm' | 'return' | 'other';
  /** media ids in the local `media` store that must upload before this item. */
  mediaIds?: string[];
  status: OutboxStatus;
  attempts: number;
  createdAt: number;
  lastAttemptAt?: number;
  nextAttemptAt?: number;
  error?: string;
  errorCode?: string;
}

export interface PendingMedia {
  id: string;
  blob: Blob;
  mimeType: string;
  purpose: string;
  /** Set once uploaded, so the parent item can reference the real media id. */
  serverMediaId?: string;
  createdAt: number;
}

const listeners = new Set<() => void>();

/** Subscribe to outbox changes so the UI badge stays accurate. */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  for (const fn of listeners) fn();
}

export function newUuid(): string {
  return crypto.randomUUID();
}

/** Queue a mutation. Returns the client UUID the server will dedupe on. */
export async function enqueue(params: {
  method: OutboxItem['method'];
  path: string;
  body: Record<string, unknown>;
  label: string;
  kind: OutboxItem['kind'];
  mediaIds?: string[];
}): Promise<string> {
  const clientUuid = newUuid();

  const item: OutboxItem = {
    id: clientUuid,
    clientUuid,
    method: params.method,
    path: params.path,
    // The UUID travels with the payload — that is what makes the retry safe.
    body: { ...params.body, clientUuid, createdOffline: true },
    label: params.label,
    kind: params.kind,
    mediaIds: params.mediaIds,
    status: 'pending',
    attempts: 0,
    createdAt: Date.now(),
  };

  await idb.put('outbox', item);
  notify();
  return clientUuid;
}

/** Store a photo locally until its parent item syncs. */
export async function stashMedia(blob: Blob, purpose: string): Promise<string> {
  const id = newUuid();
  await idb.put<PendingMedia>('media', {
    id, blob, mimeType: blob.type, purpose, createdAt: Date.now(),
  });
  return id;
}

export async function getStashedMedia(id: string): Promise<PendingMedia | undefined> {
  return idb.get<PendingMedia>('media', id);
}

export async function list(): Promise<OutboxItem[]> {
  const items = await idb.byIndex<OutboxItem>('outbox', 'byCreatedAt');
  return items.filter((i) => i.status !== 'done');
}

export async function counts(): Promise<{ pending: number; failed: number; syncing: number }> {
  const items = await list();
  return {
    pending: items.filter((i) => i.status === 'pending').length,
    syncing: items.filter((i) => i.status === 'syncing').length,
    failed: items.filter((i) => i.status === 'failed').length,
  };
}

export async function remove(id: string): Promise<void> {
  await idb.del('outbox', id);
  notify();
}

/** Reset a failed item so the next drain retries it. */
export async function retry(id: string): Promise<void> {
  const item = await idb.get<OutboxItem>('outbox', id);
  if (!item) return;
  await idb.put<OutboxItem>('outbox', {
    ...item, status: 'pending', attempts: 0, error: undefined, errorCode: undefined,
    nextAttemptAt: undefined,
  });
  notify();
}

// ---------------------------------------------------------------------------
// Draining
// ---------------------------------------------------------------------------

type Sender = (item: OutboxItem) => Promise<
  | { ok: true }
  | { ok: false; permanent: boolean; message: string; code?: string }
>;

let draining = false;

/**
 * Drain the queue.
 *
 * Serial by design, and it STOPS at the first transient failure. Continuing
 * past one would let a later order reach the server before an earlier one,
 * which breaks the ordering the whole design depends on.
 */
export async function drain(send: Sender): Promise<{ sent: number; failed: number; stopped: boolean }> {
  if (draining) return { sent: 0, failed: 0, stopped: false };
  if (!navigator.onLine) return { sent: 0, failed: 0, stopped: true };

  draining = true;
  let sent = 0;
  let failed = 0;
  let stopped = false;

  try {
    const items = (await list())
      .filter((i) => i.status === 'pending')
      .filter((i) => !i.nextAttemptAt || i.nextAttemptAt <= Date.now())
      .sort((a, b) => a.createdAt - b.createdAt);

    for (const item of items) {
      await idb.put<OutboxItem>('outbox', { ...item, status: 'syncing', lastAttemptAt: Date.now() });
      notify();

      const result = await send(item);

      if (result.ok) {
        await idb.del('outbox', item.id);
        // Photos are only discarded once their parent record is safely on the
        // server. Deleting them earlier would lose the evidence if the parent
        // then failed.
        for (const mediaId of item.mediaIds ?? []) await idb.del('media', mediaId);
        sent++;
        notify();
        continue;
      }

      const attempts = item.attempts + 1;

      if (result.permanent) {
        // A validation error will never succeed on retry. Surface it.
        await idb.put<OutboxItem>('outbox', {
          ...item, status: 'failed', attempts,
          error: result.message, errorCode: result.code,
        });
        failed++;
        notify();
        continue;
      }

      // Transient: back off and stop the run to preserve ordering.
      const backoffMs = Math.min(2 ** attempts * 1000, 5 * 60 * 1000);
      await idb.put<OutboxItem>('outbox', {
        ...item,
        status: attempts >= 8 ? 'failed' : 'pending',
        attempts,
        nextAttemptAt: Date.now() + backoffMs,
        error: result.message,
        errorCode: result.code,
      });
      if (attempts >= 8) failed++;
      stopped = true;
      notify();
      break;
    }
  } finally {
    draining = false;
    notify();
  }

  return { sent, failed, stopped };
}

/**
 * Start draining automatically.
 *
 * Triggered on reconnect, when the tab becomes visible, and on a slow timer.
 * The visibility trigger matters most in practice: a salesperson pockets the
 * phone in a dead zone and pulls it out again on the street with signal.
 */
export function startAutoDrain(send: Sender): () => void {
  const run = () => { void drain(send); };

  window.addEventListener('online', run);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') run();
  });
  const timer = window.setInterval(run, 30_000);
  run();

  return () => {
    window.removeEventListener('online', run);
    window.clearInterval(timer);
  };
}
